"use client"

import { useState, useEffect } from "react";
import { X, Wifi, CheckCircle, AlertTriangle, XCircle } from "lucide-react";
import { getSpeedTest } from "../socket";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTheme } from "../contexts/ThemeContext";
import "../componentStyle/network-diagnostic.css";

export function NetworkDiagnosticModal({ isOpen, onClose, autoStartTest = null }) {
    const { isDarkTheme } = useTheme();
    const [currentStep, setCurrentStep] = useState("selection") // selection, loading, results
    const [selectedTest, setSelectedTest] = useState("")
    const [loadingProgress, setLoadingProgress] = useState(0)
    const [loadingStage, setLoadingStage] = useState("")
    const [testResults, setTestResults] = useState(null)
    const [isTestRunning, setIsTestRunning] = useState(false)

    const testOptions = [
        // { id: "latency", label: "Latency Test" },
        { id: "speed", label: "Speed Test" },
        // { id: "connectivity", label: "Internet Connectivity" },
        // { id: "stability", label: "Network Stability" },
    ]

    const handleTestSelection = (testId) => {
        setSelectedTest(testId)
    }

    const startTest = async () => {
        if (!selectedTest || isTestRunning) return

        setIsTestRunning(true)
        setCurrentStep("loading")
        setLoadingProgress(0)
        setLoadingStage("Initializing...")

        try {
            let results
            switch (selectedTest) {
                case "latency":
                    results = await runLatencyTestAPI()
                    break
                case "speed":
                    results = await runSpeedTestAPI()
                    break
                case "connectivity":
                    results = await runConnectivityTest()
                    break
                case "stability":
                    results = await runStabilityTest()
                    break
                default:
                    throw new Error("Unknown test type")
            }

            setTestResults(results)
            setCurrentStep("results")
        } catch (error) {
            console.error("Test failed:", error)
            setTestResults({
                title: "Test Failed",
                status: "error",
                statusText: "Unable to complete the test. Please check your connection and try again.",
                data: [{ label: "Error", value: error.message, status: "error" }],
            })
            setCurrentStep("results")
        } finally {
            setIsTestRunning(false)
            setLoadingProgress(100)
            setLoadingStage("Complete!")
        }
    }

    // API-based Latency Test (Ping Test)
    const runLatencyTestAPI = async () => {
        setLoadingStage("Testing Latency...")
        setLoadingProgress(20)

        try {
            const response = await fetch(`${getSpeedTest()}/ping`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    samples: 15, // More samples for better accuracy
                }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            setLoadingProgress(80)
            const result = await response.json()

            if (!result.success) {
                throw new Error(result.message || "Ping test failed")
            }

            setLoadingProgress(100)

            const pingData = result.data.ping
            const clientData = result.data.client

            const getLatencyStatus = (latency) => {
                if (latency <= 50) return "good"
                if (latency <= 100) return "warning"
                return "error"
            }

            const getJitterStatus = (jitter) => {
                if (jitter <= 20) return "good"
                if (jitter <= 50) return "warning"
                return "error"
            }

            const overallStatus =
                pingData.latency <= 50 && pingData.jitter <= 20
                    ? "success"
                    : pingData.latency <= 100 && pingData.jitter <= 50
                        ? "warning"
                        : "error"

            return {
                title: "Latency Test Results",
                status: overallStatus,
                statusText:
                    overallStatus === "success"
                        ? "Excellent connection latency"
                        : overallStatus === "warning"
                            ? "Good connection latency"
                            : "High latency detected",
                data: [
                    {
                        label: "Average Latency",
                        value: `${Math.round(pingData.latency)}ms`,
                        status: getLatencyStatus(pingData.latency),
                    },
                    {
                        label: "Minimum Latency",
                        value: `${pingData.min}ms`,
                        status: getLatencyStatus(pingData.min),
                    },
                    {
                        label: "Maximum Latency",
                        value: `${pingData.max}ms`,
                        status: getLatencyStatus(pingData.max),
                    },
                    {
                        label: "Jitter",
                        value: `${Math.round(pingData.jitter)}ms`,
                        status: getJitterStatus(pingData.jitter),
                    },
                ],
            }
        } catch (error) {
            throw new Error(`Latency test failed: ${error.message}`)
        }
    }

    // API-based Speed Test (Download and Upload separately)
    const runSpeedTestAPI = async (signal) => {
        let progressInterval
        const abortController = new AbortController()
        let currentProgress = 0
        let targetProgress = 0
        let isTestComplete = false

        // Smooth progress animation function
        const updateProgress = () => {
            if (isTestComplete) return

            // Smooth interpolation towards target progress
            const diff = targetProgress - currentProgress
            if (Math.abs(diff) > 0.5) {
                currentProgress += diff * 0.1 // Smooth interpolation factor
                setLoadingProgress(Math.min(Math.round(currentProgress), 99)) // Cap at 99% until complete
            } else {
                currentProgress = targetProgress
                setLoadingProgress(Math.min(Math.round(currentProgress), 99))
            }
        }

        try {
            // Stage 1: Initialization
            setLoadingStage("Initializing Speed Test...")
            currentProgress = 0
            targetProgress = 10
            setLoadingProgress(0)

            // Start smooth progress animation
            progressInterval = setInterval(updateProgress, 50) // 20 FPS for smooth animation

            // Wait for initialization
            await new Promise(resolve => setTimeout(resolve, 500))
            targetProgress = 15

            // Test Download Speed
            setLoadingStage("Testing Download Speed...")
            targetProgress = 25

            const downloadResponse = await fetch(`${getSpeedTest()}/download`, {
                method: "POST",
                signal: signal || abortController.signal,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    size: "large",
                    duration: 15000,
                }),
            })

            if (!downloadResponse.ok) {
                throw new Error(`Download test failed: HTTP ${downloadResponse.status}`)
            }

            targetProgress = 60

            const downloadResult = await downloadResponse.json()
            if (!downloadResult?.success) {
                throw new Error(downloadResult?.message || "Download test failed")
            }

            targetProgress = 70

            // Test Upload Speed
            setLoadingStage("Testing Upload Speed...")
            targetProgress = 80

            const uploadResponse = await fetch(`${getSpeedTest()}/upload`, {
                method: "POST",
                signal: signal || abortController.signal,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    size: "large",
                    duration: 15000,
                }),
            })

            if (!uploadResponse.ok) {
                throw new Error(`Upload test failed: HTTP ${uploadResponse.status}`)
            }

            targetProgress = 90

            const uploadResult = await uploadResponse.json()
            if (!uploadResult?.success) {
                throw new Error(uploadResult?.message || "Upload test failed")
            }

            // Final stage
            setLoadingStage("Analyzing Results...")
            targetProgress = 95

            // Wait for final processing
            await new Promise(resolve => setTimeout(resolve, 300))

            // Complete the test
            isTestComplete = true
            clearInterval(progressInterval)
            setLoadingProgress(100)
            toast.success("Speed test completed successfully")

            const downloadData = downloadResult?.data?.download || {}
            const uploadData = uploadResult?.data?.upload || {}

            const getSpeedStatus = (speed) =>
                speed >= 25 ? "good" : speed >= 10 ? "warning" : "error"

            return {
                title: "Speed Test Results",
                status: "success",
                statusText: "Speed test completed successfully",
                data: [
                    {
                        label: "Download Speed",
                        value: `${downloadData?.mbps ? (downloadData.mbps / 8).toFixed(2) : "--"
                            } MB/s`,
                        status: getSpeedStatus(downloadData?.mbps / 8 || 0),
                    },
                    {
                        label: "Upload Speed",
                        value: `${uploadData?.mbps ? (uploadData.mbps / 8).toFixed(2) : "--"
                            } MB/s`,
                        status: getSpeedStatus(uploadData?.mbps / 8 || 0),
                    },
                ],
            };

        } catch (error) {
            isTestComplete = true
            clearInterval(progressInterval)

            if (error.name === "AbortError") {
                toast.warn("Speed test was cancelled")
            } else {
                toast.error("Speed test failed: " + error.message)
            }
            throw error
        } finally {
            clearInterval(progressInterval)
        }
    }



    // Real Connectivity Test (keeping original implementation)
    const runConnectivityTest = async () => {
        const tests = [
            { name: "DNS Resolution", test: testDNS },
            { name: "HTTP Connectivity", test: testHTTP },
            { name: "HTTPS Connectivity", test: testHTTPS },
            { name: "Packet Loss", test: testPacketLoss },
        ]

        const results = []
        let overallStatus = "success"

        for (let i = 0; i < tests.length; i++) {
            setLoadingStage(`Testing ${tests[i].name}...`)
            try {
                const result = await tests[i].test()
                results.push({
                    label: tests[i].name,
                    value: result.value,
                    status: result.status,
                })

                if (result.status === "error") overallStatus = "error"
                else if (result.status === "warning" && overallStatus !== "error") overallStatus = "warning"

                setLoadingProgress(((i + 1) / tests.length) * 100)

                // Small delay between tests
                await new Promise((resolve) => setTimeout(resolve, 300))
            } catch (error) {
                results.push({
                    label: tests[i].name,
                    value: "Failed",
                    status: "error",
                })
                overallStatus = "error"
            }
        }

        return {
            title: "Internet Connectivity Results",
            status: overallStatus,
            statusText:
                overallStatus === "success"
                    ? "All connectivity tests passed"
                    : overallStatus === "warning"
                        ? "Some connectivity issues detected"
                        : "Connectivity problems detected",
            data: results,
        }
    }

    // DNS Resolution Test
    const testDNS = async () => {
        try {
            const startTime = performance.now()
            const testDomains = ["google.com", "cloudflare.com", "github.com"]
            let successCount = 0

            for (const domain of testDomains) {
                try {
                    await fetch(`https://${domain}/favicon.ico`, {
                        method: "HEAD",
                        mode: "no-cors",
                        cache: "no-cache",
                    })
                    successCount++
                } catch (error) {
                    successCount++
                }
            }

            const endTime = performance.now()
            const responseTime = Math.round(endTime - startTime)

            if (successCount === testDomains.length) {
                return { value: `Working (${responseTime}ms)`, status: "good" }
            } else if (successCount > 0) {
                return { value: `Partial (${successCount}/${testDomains.length})`, status: "warning" }
            } else {
                return { value: "Failed", status: "error" }
            }
        } catch (error) {
            return { value: "Failed", status: "error" }
        }
    }

    // HTTP Connectivity Test
    const testHTTP = async () => {
        try {
            const response = await fetch("http://httpbin.org/get", {
                cache: "no-cache",
                signal: AbortSignal.timeout(5000),
            })
            return {
                value: response.ok ? "Working" : "Limited",
                status: response.ok ? "good" : "warning",
            }
        } catch (error) {
            return { value: "Blocked/Failed", status: "error" }
        }
    }

    // HTTPS Connectivity Test
    const testHTTPS = async () => {
        try {
            const response = await fetch("https://httpbin.org/get", {
                cache: "no-cache",
                signal: AbortSignal.timeout(5000),
            })
            return {
                value: response.ok ? "Working" : "Limited",
                status: response.ok ? "good" : "warning",
            }
        } catch (error) {
            return { value: "Blocked/Failed", status: "error" }
        }
    }

    // Packet Loss Test
    const testPacketLoss = async () => {
        const testCount = 10
        const testUrls = [
            "https://www.google.com/favicon.ico",
            "https://www.cloudflare.com/favicon.ico",
            "https://httpbin.org/delay/0",
        ]

        let successCount = 0

        for (let i = 0; i < testCount; i++) {
            try {
                const url = testUrls[i % testUrls.length]
                await fetch(`${url}?t=${Date.now()}`, {
                    method: "HEAD",
                    mode: "no-cors",
                    cache: "no-cache",
                    signal: AbortSignal.timeout(3000),
                })
                successCount++
            } catch (error) {
                // Packet lost or timeout
            }

            await new Promise((resolve) => setTimeout(resolve, 100))
        }

        const packetLoss = ((testCount - successCount) / testCount) * 100

        return {
            value: `${packetLoss.toFixed(1)}%`,
            status: packetLoss === 0 ? "good" : packetLoss <= 5 ? "warning" : "error",
        }
    }

    // Real Stability Test (keeping original implementation)
    const runStabilityTest = async () => {
        const testDuration = 10000 // 10 seconds
        const testInterval = 500 // Test every 500ms
        const testCount = testDuration / testInterval

        let successfulTests = 0
        let totalResponseTime = 0
        let disconnections = 0
        let lastTestSuccessful = true

        setLoadingStage("Testing Network Stability...")

        for (let i = 0; i < testCount; i++) {
            try {
                const startTime = performance.now()

                await fetch("https://httpbin.org/delay/0", {
                    method: "HEAD",
                    cache: "no-cache",
                    signal: AbortSignal.timeout(2000),
                })

                const endTime = performance.now()
                const responseTime = endTime - startTime

                successfulTests++
                totalResponseTime += responseTime

                if (!lastTestSuccessful) {
                    lastTestSuccessful = true
                }
            } catch (error) {
                if (lastTestSuccessful) {
                    disconnections++
                    lastTestSuccessful = false
                }
            }

            setLoadingProgress((i / testCount) * 100)
            await new Promise((resolve) => setTimeout(resolve, testInterval))
        }

        const uptime = (successfulTests / testCount) * 100
        const avgResponseTime = successfulTests > 0 ? totalResponseTime / successfulTests : 0

        let stabilityScore = "Poor"
        let status = "error"

        if (uptime >= 98 && disconnections <= 1) {
            stabilityScore = "Excellent"
            status = "success"
        } else if (uptime >= 95 && disconnections <= 3) {
            stabilityScore = "Good"
            status = "success"
        } else if (uptime >= 90) {
            stabilityScore = "Fair"
            status = "warning"
        }

        return {
            title: "Network Stability Results",
            status: status,
            statusText:
                status === "success"
                    ? "Network connection is stable"
                    : status === "warning"
                        ? "Network has some stability issues"
                        : "Network connection is unstable",
            data: [
                {
                    label: "Connection Uptime",
                    value: `${uptime.toFixed(1)}%`,
                    status: uptime >= 98 ? "good" : uptime >= 95 ? "warning" : "error",
                },
                {
                    label: "Stability Score",
                    value: stabilityScore,
                    status: status,
                },
                {
                    label: "Disconnections",
                    value: disconnections.toString(),
                    status: disconnections <= 1 ? "good" : disconnections <= 3 ? "warning" : "error",
                },
                {
                    label: "Avg Response Time",
                    value: `${Math.round(avgResponseTime)}ms`,
                    status: avgResponseTime <= 100 ? "good" : avgResponseTime <= 300 ? "warning" : "error",
                },
            ],
        }
    }

    const resetModal = () => {
        setCurrentStep("selection")
        setSelectedTest("")
        setLoadingProgress(0)
        setLoadingStage("")
        setTestResults(null)
        setIsTestRunning(false)
    }

    const handleClose = () => {
        resetModal()
        onClose()
    }

    const runAnotherTest = () => {
        setCurrentStep("selection")
        setSelectedTest("")
        setLoadingProgress(0)
        setLoadingStage("")
        setTestResults(null)
        setIsTestRunning(false)
    }

    useEffect(() => {
        if (!isOpen) {
            resetModal()
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen && autoStartTest && !isTestRunning) {
            setSelectedTest(autoStartTest)
        }
    }, [isOpen, autoStartTest, isTestRunning])

    if (!isOpen) return null

    return (
        <div className="modal-overlay-network active">
            <div className={`modal-container-network ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
                {/* Header */}
                <div className="modal-header-network">
                    <div className="modal-header-icon-network">
                        <Wifi />
                    </div>
                    <div className="modal-header-network-title">
                        <h2 className="modal-title-network">Network Diagnostic</h2>
                        <p className="modal-subtitle-network">Comprehensive network analysis and testing</p>
                    </div>
                    <button className="modal-close-network" onClick={handleClose}>
                        <X />
                    </button>
                </div>

                {/* Body */}
                <div className="modal-body-network">
                    {currentStep === "selection" && (
                        <>
                            <h3 className="diagnostic-section-title-network">Diagnostic Results</h3>
                            <div className="test-options-network">
                                {testOptions.map((option) => (
                                    <div
                                        key={option.id}
                                        className={`test-option-network ${selectedTest === option.id ? "selected" : ""}`}
                                        onClick={() => handleTestSelection(option.id)}
                                    >
                                        <div className="test-option-radio-network"></div>
                                        <span className="test-option-label-network">{option.label}</span>
                                    </div>
                                ))}
                            </div>
                            <button className="start-test-button-network" onClick={startTest} disabled={!selectedTest || isTestRunning}>
                                {isTestRunning ? "Running Test..." : "Start test"}
                            </button>
                        </>
                    )}

                    {currentStep === "loading" && (
                        <div className="loading-container-network">
                            <h3 className="loading-title-network">Running {testOptions.find((t) => t.id === selectedTest)?.label}</h3>
                            <p className="loading-subtitle-network">{loadingStage}</p>
                            <div className="loading-bar-container-network">
                                <div className="loading-bar-network" style={{ width: `${loadingProgress}%` }}></div>
                            </div>
                            <div className="loading-percentage-network">{Math.round(loadingProgress)}%</div>
                        </div>
                    )}

                    {currentStep === "results" && testResults && (
                        <div className="results-container-network">
                            <h3 className="results-title-network">
                                {testResults.status === "success" && <CheckCircle className="results-icon-network" />}
                                {testResults.status === "warning" && <AlertTriangle className="results-icon-network" />}
                                {testResults.status === "error" && <XCircle className="results-icon-network" />}
                                {testResults.title}
                            </h3>

                            <div className="results-grid-network">
                                {testResults.data.map((item, index) => (
                                    <div key={index} className="result-item-network">
                                        <span className="result-label-network">{item.label}</span>
                                        <span className={`result-value-network ${item.status}`}>{item.value}</span>
                                    </div>
                                ))}
                            </div>

                            <div className={`result-status-network ${testResults.status}`}>
                                {testResults.status === "success" && <CheckCircle className="result-status-icon-network" />}
                                {testResults.status === "warning" && <AlertTriangle className="result-status-icon-network" />}
                                {testResults.status === "error" && <XCircle className="result-status-icon-network" />}
                                <span>{testResults.statusText}</span>
                            </div>

                            <div className="modal-actions-network">
                                <button className="action-button-network" onClick={runAnotherTest}>
                                    Run Another Test
                                </button>
                                <button className="action-button-network primary" onClick={handleClose}>
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

}
